import { Fingering } from '../types/fingering';
import { ChordQuality } from '../data/chords';
import library from '../data/chord-library.json';
import { pitchClassToSemi } from './midi';

// ─── Library types (subset, matching the JSON shape) ──────────────────────────

interface LibChord {
  id: string;
  tuning: string;
  name: string;
  root: string;
  quality: string;
  inversion: 'root' | '1st' | '2nd' | '3rd';
  kind: 'open' | 'barre' | 'triad' | 'voicing';
  shape: string | null;
  frets: Array<number | 'x'>;
  fingers: Array<number | 'T' | null>;
  barre: { fret: number; strings: [number, number] } | null;
  base_fret: number;
  notes: Array<string | null>;
  intervals_played: Array<string | null>;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  aliases: string[];
}

interface LibraryShape {
  id: string;
  tuning: string;
  quality: string;
  pattern: Array<number | 'x'>;
  fingers: Array<number | 'T' | null>;
  barre: { fret_offset: number; strings: [number, number] } | null;
  root_string_index: number;
  intervals_played: Array<string | null>;
  difficulty: LibChord['difficulty'];
}

interface LibraryRoot {
  tunings: Array<{ id: string; name: string; open_pitches: string[]; open_notes: string[]; string_count: number }>;
  shapes:  LibraryShape[];
  chords:  LibChord[];
}

const LIB = library as unknown as LibraryRoot;

// ─── Translation tables ───────────────────────────────────────────────────────

const TUNING_ID_APP_TO_LIB: Record<string, string> = {
  'standard': 'standard',
  'drop-d':   'drop_d',
  'dadgad':   'dadgad',
  'open-g':   'open_g',
  'open-d':   'open_d',
};

const QUALITY_APP_TO_LIB: Record<ChordQuality, string> = {
  maj:     'major',
  min:     'minor',
  '5':     '5',
  dom7:    '7',
  min7:    'm7',
  maj7:    'maj7',
  sus2:    'sus2',
  sus4:    'sus4',
  add9:    'add9',
  '6':     '6',
  min6:    'm6',
  '9':     '9',
  maj9:    'maj9',
  min9:    'm9',
  dim:     'dim',
  aug:     'aug',
  dim7:    'dim7',
  min7b5:  'm7b5',
  '13':    '13',
  // ── Tier 4: Guitar Chord Bible additions (data-backed) ──
  dom7sus4:   '7sus4',
  dom7s5:     '7#5',
  dom7b9:     '7b9',
  dom7s9:     '7#9',
};

const FLAT_TO_SHARP_ROOT: Record<string, string> = {
  'Bb': 'A#', 'Eb': 'D#', 'Ab': 'G#', 'Db': 'C#', 'Gb': 'F#',
};

const INVERSION_TO_NUM: Record<LibChord['inversion'], 0 | 1 | 2 | 3> = {
  'root': 0, '1st': 1, '2nd': 2, '3rd': 3,
};

const DIFFICULTY_RANK: Record<LibChord['difficulty'], number> = {
  beginner: 0, intermediate: 1, advanced: 2,
};

// ─── Public API ───────────────────────────────────────────────────────────────

export function tuningCoveredByLibrary(tuningId: string): boolean {
  return tuningId in TUNING_ID_APP_TO_LIB;
}

// Lazy index: built on first call, cached for the session.
let chordIndex: Map<string, Map<string, LibChord[]>> | null = null;
let shapeIndex: Map<string, Map<string, LibraryShape[]>> | null = null;

const PRACTICAL_MAX_FRET = 15;

function buildIndex(): Map<string, Map<string, LibChord[]>> {
  const idx = new Map<string, Map<string, LibChord[]>>();
  for (const c of LIB.chords) {
    if (!idx.has(c.tuning)) idx.set(c.tuning, new Map());
    const inner = idx.get(c.tuning)!;
    const key = `${c.root}_${c.quality}`;
    if (!inner.has(key)) inner.set(key, []);
    inner.get(key)!.push(c);
  }
  return idx;
}

function buildShapeIndex(): Map<string, Map<string, LibraryShape[]>> {
  const idx = new Map<string, Map<string, LibraryShape[]>>();
  for (const s of LIB.shapes) {
    if (!idx.has(s.tuning)) idx.set(s.tuning, new Map());
    const inner = idx.get(s.tuning)!;
    if (!inner.has(s.quality)) inner.set(s.quality, []);
    inner.get(s.quality)!.push(s);
  }
  return idx;
}

export function lookupChord(tuningId: string, root: string, quality: ChordQuality): Fingering[] {
  const libTuning = TUNING_ID_APP_TO_LIB[tuningId];
  if (!libTuning) return [];

  const libQuality = QUALITY_APP_TO_LIB[quality];
  if (!libQuality) return [];

  const libRoot = FLAT_TO_SHARP_ROOT[root] ?? root;

  if (!chordIndex) chordIndex = buildIndex();
  if (!shapeIndex) shapeIndex = buildShapeIndex();

  const key = `${libRoot}_${libQuality}`;
  const directHits = chordIndex.get(libTuning)?.get(key) ?? [];
  const directFingerings: Array<Fingering & { __difficulty?: string }> = directHits.map(translateChordEntry);

  // Shape transposition pass — each shape can yield multiple Fingerings (octave variants).
  const shapesForQuality = shapeIndex.get(libTuning)?.get(libQuality) ?? [];
  const shapeFingerings: Array<Fingering & { __difficulty?: string }> = [];
  for (const s of shapesForQuality) {
    const fingeringsFromShape = applyShape(s, libRoot, libTuning);
    for (const f of fingeringsFromShape) {
      if (validateTransposition(f, libRoot, s, libTuning)) {
        shapeFingerings.push({ ...f, __difficulty: s.difficulty });
      }
    }
  }

  // Combine with dedupe (direct entries win over shape-derived on identical frets).
  const seen = new Set<string>();
  const combined: Array<Fingering & { __difficulty?: string }> = [];
  for (const f of [...directFingerings, ...shapeFingerings]) {
    const fk = f.frets.join(',');
    if (seen.has(fk)) continue;
    seen.add(fk);
    combined.push(f);
  }

  // Sort: inversion → difficulty → startFret asc.
  combined.sort((a, b) => {
    if (a.inversion !== b.inversion) return (a.inversion ?? 0) - (b.inversion ?? 0);
    const aRank = DIFFICULTY_RANK[(a.__difficulty ?? 'intermediate') as LibChord['difficulty']];
    const bRank = DIFFICULTY_RANK[(b.__difficulty ?? 'intermediate') as LibChord['difficulty']];
    if (aRank !== bRank) return aRank - bRank;
    return a.startFret - b.startFret;
  });

  return combined.map(({ __difficulty, ...rest }) => rest as Fingering);
}

// ─── Translation helpers ──────────────────────────────────────────────────────

function translateChordEntry(c: LibChord): Fingering & { __difficulty?: string } {
  const frets = c.frets.map(f => f === 'x' ? -1 : f);
  const fingers = c.fingers.map(f => {
    if (f === null) return 0;
    if (f === 'T') return 1;   // treat thumb as index for v1.2
    return f;
  });

  const barre = c.barre ? {
    fret: c.barre.fret,
    fromString: c.barre.strings[0] + 1,
    toString:   c.barre.strings[1] + 1,
  } : undefined;

  // Lowest fretted note ≥ 1, defaulting to 1 if none.
  const startFret = (() => {
    const fretted = frets.filter(f => f >= 1);
    return fretted.length > 0 ? Math.min(...fretted) : 1;
  })();

  // Bass note: the lowest sounding string's note (for inversions).
  const bassNote = c.notes.find(n => n != null) ?? undefined;

  return {
    id: c.id,
    label: c.name,
    frets,
    fingers,
    barre,
    startFret,
    inversion: INVERSION_TO_NUM[c.inversion],
    bassNote,
    __difficulty: c.difficulty,
  };
}

// ─── Shape transposition ──────────────────────────────────────────────────────

function applyShape(
  shape: LibraryShape,
  rootPc: string,
  libTuning: string,
): Fingering[] {
  const tuningEntry = LIB.tunings.find(t => t.id === libTuning);
  if (!tuningEntry) return [];

  const openSemis = tuningEntry.open_pitches.map(pc => pitchClassToSemi(pc));
  const rootSemi = pitchClassToSemi(rootPc);
  const rsi = shape.root_string_index;
  const patternRoot = shape.pattern[rsi];
  if (typeof patternRoot !== 'number') return [];

  // N = (rootSemi − openSemi[rsi] − pattern[rsi]) mod 12 — canonical root position.
  const baseN = ((rootSemi - openSemis[rsi] - patternRoot) % 12 + 12) % 12;

  const fingers = shape.fingers.map(f => {
    if (f === null) return 0;
    if (f === 'T') return 1;
    return f;
  }) as [number, number, number, number, number, number];

  const out: Fingering[] = [];

  // Generate the canonical voicing plus every octave-up variant (N+12, N+24, ...)
  // whose max applied fret stays ≤ PRACTICAL_MAX_FRET.
  for (let k = 0; ; k++) {
    const N = baseN + k * 12;

    const frets: number[] = [];
    for (const p of shape.pattern) {
      if (p === 'x') frets.push(-1);
      else frets.push(p + N);
    }

    const fretMax = Math.max(...frets);
    if (fretMax > PRACTICAL_MAX_FRET) break;

    const barre = shape.barre ? {
      fret: shape.barre.fret_offset + N,
      fromString: shape.barre.strings[0] + 1,
      toString:   shape.barre.strings[1] + 1,
    } : undefined;

    const startFret = (() => {
      const fretted = frets.filter(f => f >= 1);
      return fretted.length > 0 ? Math.min(...fretted) : 1;
    })();

    const id = k === 0
      ? `${shape.id}_${rootPc}`
      : `${shape.id}_${rootPc}_oct${k}`;

    out.push({
      id,
      label: shape.id.replace(/_/g, ' '),
      frets: frets as [number, number, number, number, number, number],
      fingers,
      barre,
      startFret,
      inversion: 0,             // shapes are root-position only per library spec
    });
  }

  return out;
}

function validateTransposition(
  fingering: Fingering,
  expectedRootPc: string,
  shape: LibraryShape,
  libTuning: string,
): boolean {
  const tuningEntry = LIB.tunings.find(t => t.id === libTuning);
  if (!tuningEntry) return false;
  const openSemis = tuningEntry.open_pitches.map(pc => pitchClassToSemi(pc));

  const rsi = shape.root_string_index;
  const fret = fingering.frets[rsi];
  if (fret < 0) return false;
  const noteSemi = (openSemis[rsi] + fret) % 12;
  const expectedSemi = pitchClassToSemi(expectedRootPc);
  if (noteSemi !== expectedSemi) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(`Transposition validation failed: shape ${shape.id} for root ${expectedRootPc} produced wrong note at string ${rsi}`);
    }
    return false;
  }
  return true;
}
